"use client";

import { useRef, useState } from "react";

import cn from "classnames";

import { PauseIcon } from "@/components/icons/pause.icon";
import { PlayIcon } from "@/components/icons/play.icon";

import type { SocialProofClip, VideoPhase } from "./adtSocialProof.types";
import { AdtSocialProofCarousel } from "./adtSocialProofCarousel.component";

const VIDEO_PRELOAD = "metadata";

export function AdtSocialProofVideo({
  clips,
  playLabel,
  pauseLabel,
  previousLabel,
  nextLabel,
  unavailableLabel,
  stretch = false,
}: AdtSocialProofVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<VideoPhase>("idle");
  const clip = clips[index] ?? clips[0];
  const actionLabel = phase === "playing" ? pauseLabel : playLabel;

  const showFrame = () => {
    const video = videoRef.current;
    if (!video || video.currentTime > 0) return;
    try {
      video.currentTime = 0.001;
    } catch {
      // Some browsers reject a seek before the frame is ready.
    }
  };

  const select = (next: number) => {
    videoRef.current?.pause();
    setPhase("idle");
    setIndex(next);
  };

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video || !clip.videoUrl) {
      setPhase("error");
      return;
    }

    if (phase === "playing") {
      video.pause();
      setPhase("paused");
      return;
    }

    setPhase("playing");
    try {
      const pending = video.play();
      void Promise.resolve(pending)
        .then(() => {
          const track = video.textTracks?.[0];
          if (track) track.mode = "showing";
        })
        .catch(() => setPhase("error"));
    } catch {
      setPhase("error");
    }
  };

  return (
    <AdtSocialProofCarousel
      count={clips.length}
      index={index}
      onSelect={select}
      previousLabel={previousLabel}
      nextLabel={nextLabel}
      itemLabel="Video"
      groupLabel="Customer videos"
      placement="overlay-desktop"
      testId="social-proof-videos">
      <div
        className={cn(
          "bg-secondary relative aspect-[4/5] w-full overflow-hidden",
          stretch && "lg:absolute lg:inset-0 lg:aspect-auto lg:h-full",
        )}
        data-testid="social-proof-video-frame"
        data-loaded-video={clip.videoUrl ? "1" : "0"}>
        <video
          key={clip._key}
          ref={videoRef}
          data-testid="social-proof-video"
          src={clip.videoUrl}
          preload={VIDEO_PRELOAD}
          playsInline
          crossOrigin="anonymous"
          aria-label={playLabel}
          onLoadedData={showFrame}
          onEnded={() => setPhase("paused")}
          onError={() => setPhase("error")}
          className="absolute inset-0 z-10 h-full w-full object-cover">
          {clip.captionsUrl ? (
            <track
              kind="captions"
              src={clip.captionsUrl}
              srcLang="en"
              label="English"
              default
            />
          ) : null}
        </video>
        {phase === "error" ? (
          <p
            role="status"
            className="absolute inset-x-4 bottom-16 z-20 text-center text-sm text-white">
            {unavailableLabel}
          </p>
        ) : null}
        <button
          type="button"
          onClick={togglePlayback}
          aria-label={actionLabel}
          data-testid="social-proof-play"
          className="text-secondary absolute top-1/2 left-1/2 z-20 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
          {phase === "playing" ? (
            <PauseIcon aria-hidden width={28} height={28} />
          ) : (
            <PlayIcon aria-hidden width={28} height={28} />
          )}
        </button>
      </div>
    </AdtSocialProofCarousel>
  );
}

type AdtSocialProofVideoProps = {
  clips: SocialProofClip[];
  playLabel: string;
  pauseLabel: string;
  previousLabel: string;
  nextLabel: string;
  unavailableLabel: string;
  stretch?: boolean;
};
